import logger from '@/logger';

const log = logger.getChild('csv-file-parser');

export const validateFile = (file: File): boolean => {
  return file.type === 'text/csv';
};

/**
 * Parse a CSV line properly handling quoted values
 * This handles CSV values that are wrapped in quotes and may contain commas
 */
interface ParseState {
  index: number;
  current: string;
  inQuotes: boolean;
}

const handleQuoteCharacter = (line: string, state: ParseState): ParseState =>
  state.inQuotes && line[state.index + 1] === '"'
    ? {
        index: state.index + 2,
        current: `${state.current}"`,
        inQuotes: state.inQuotes,
      }
    : {
        index: state.index + 1,
        current: state.current,
        inQuotes: !state.inQuotes,
      };

const processCharacter = (
  line: string,
  state: ParseState,
  result: string[],
): ParseState => {
  const char = line[state.index];

  if (char === '"') {
    return handleQuoteCharacter(line, state);
  }

  if (char === ',' && !state.inQuotes) {
    result.push(state.current.trim());
    return { index: state.index + 1, current: '', inQuotes: state.inQuotes };
  }

  return {
    index: state.index + 1,
    current: state.current + char,
    inQuotes: state.inQuotes,
  };
};

const parseCsvLine = (line: string): string[] => {
  const result: string[] = [];
  let state: ParseState = { index: 0, current: '', inQuotes: false };

  while (state.index < line.length) {
    state = processCharacter(line, state, result);
  }

  result.push(state.current.trim());
  return result;
};

// eslint-disable-next-line max-statements
export const parseFile = async (file: File): Promise<[string[], string[]]> => {
  log.debug('Parsing file', { file });
  const fileStream = file.stream();
  const reader = fileStream.getReader();
  const decoder = new TextDecoder('utf8');
  let headerLine = '';
  let firstRowLine = '';
  let done = false;
  let lineCount = 0;

  while (!done) {
    log.debug('Reading chunk...');
    const { value, done: readerDone } = await reader.read();
    log.log('Read chunk, readerDone:', readerDone);
    done = readerDone;

    const chunk = decoder.decode(value ?? new Uint8Array(), { stream: true });

    const newlineIndex = chunk.indexOf('\n');
    if (newlineIndex !== -1) {
      if (lineCount === 0) {
        headerLine += chunk.slice(0, newlineIndex);
        const remaining = chunk.slice(newlineIndex + 1);
        const remainingNewlineIndex = remaining.indexOf('\n');
        // eslint-disable-next-line max-depth
        if (remainingNewlineIndex !== -1) {
          firstRowLine += remaining.slice(0, remainingNewlineIndex);
          break;
        }
        firstRowLine += chunk.slice(newlineIndex + 1);
        lineCount++;
      } else if (lineCount === 1) {
        firstRowLine += chunk.slice(0, newlineIndex);
        break;
      }
    } else {
      if (lineCount === 0) {
        headerLine += chunk;
      } else if (lineCount === 1) {
        firstRowLine += chunk;
      }
    }
  }

  log.log('Parsed file', { headerLine, firstRowLine });

  return [
    parseCsvLine(headerLine.replaceAll('\r', '')),
    parseCsvLine(firstRowLine.replaceAll('\r', '')),
  ];
};

export const hasDuplicates = <T>(headers: T[]): boolean => {
  log.debug('Checking for duplicates', { headers });
  const set = new Set<T>();

  for (const header of headers) {
    if (set.has(header)) {
      log.debug('Found duplicates');
      return true;
    }
    set.add(header);
  }

  log.debug('No duplicates found');

  return false;
};

export const validateHeaders = async (
  file: File,
): Promise<[string[], string[]]> => {
  const [parsedHeaders, parsedValues] = await parseFile(file);

  const valid =
    parsedHeaders.length >= 3 &&
    parsedHeaders.every((header) => header.trim().length > 0) &&
    parsedHeaders.length === parsedValues.length &&
    !hasDuplicates(parsedHeaders);

  if (!valid) {
    throw new Error('Invalid headers');
  }

  return [parsedHeaders, parsedValues];
};
